import React from 'react';
import { getReviewsGroupRatingTotalByGroupId } from 'features/reviews/selectors';
import { Rating as FrontonRating, LinkButton, RatingProps } from 'fronton-react';
import { isMoscowRegion } from 'utils/is-moscow-region';
import { connect } from 'react-redux';
import type { Dispatch, Action } from 'redux';
import { RootReducer } from 'store/root-reducer';
import { isRatingValueValid, isNumberOfRatingsValid } from 'utils/domain-specifics/reviews/validation';
import { getRegionId } from 'features/page-model/ducks/get-model';
import { analyticsUtils, getLang } from 'utils';
import { addReviewsLabel } from './styles';
import { roundRating } from 'features/product-info/utils/round-rating';
import { getOtzyvyUrl } from 'utils/get-otzyvy-url';
import { useForceRenderEffect } from './hooks/use-force-render-effect';

const REVIEWS_ANCHOR = '#reviews';

type ReviewsTotalScoreMappedState = {
  totalScore: number | null;
  reviewsCount: number | null;
  isMoscow: boolean;
};

type ReviewsTotalScoreProps = ReviewsTotalScoreMappedState & React.HTMLAttributes<HTMLDivElement>;

export const ReviewsTotalScore: React.FC<ReviewsTotalScoreProps> = ({
  reviewsCount,
  totalScore,
  isMoscow,
  className = '',
  ...htmlProps
}) => {
  const lang = getLang();
  const clickHandler = (e: React.MouseEvent) => {
    e.preventDefault();
    document.querySelector(REVIEWS_ANCHOR)?.scrollIntoView({ behavior: 'smooth' });
    analyticsUtils.sendEvent('GA:clickButton', {
      elementBlock: 'productDetail',
      elementName: 'productRating',
      elementText: getReviewsText(reviewsCount || 0, lang),
      elementType: 'button',
    });
  };
  //NOTE: href нельзя удалять, т.к. это сломает индексацию отзывов по товару поисковыми ботами
  const reviewsRedirect = getOtzyvyUrl(isMoscow, reviewsCount);

  let content = <RatingStars totalScore={0} reviewsCount={0} />;
  if (
    typeof totalScore === 'number' &&
    isRatingValueValid(totalScore) &&
    typeof reviewsCount === 'number' &&
    isNumberOfRatingsValid(reviewsCount) &&
    reviewsCount > 0
  ) {
    content = <RatingStars totalScore={totalScore} reviewsCount={reviewsCount} />;
  }

  return (
    <div className={className} {...htmlProps}>
      <LinkButton onClick={clickHandler} href={reviewsRedirect} variant="secondary">
        {content}
      </LinkButton>
    </div>
  );
};

export const mapStoreToProps = (store: RootReducer): ReviewsTotalScoreMappedState => {
  const totalScoreMeta = getReviewsGroupRatingTotalByGroupId(store);
  const regionCode = getRegionId(store);
  const isMoscow = isMoscowRegion(regionCode);

  return {
    totalScore: totalScoreMeta?.scoreRating ?? null,
    reviewsCount: totalScoreMeta?.countReview ?? null,
    isMoscow,
  };
};

export const PdpReviewsTotalScore = connect(mapStoreToProps)(({
  dispatch,
  ...restProps
}: { dispatch: Dispatch<Action> } & React.ComponentProps<typeof ReviewsTotalScore>) => {
  return <ReviewsTotalScore {...restProps} />;
});

const reviewsPluralRule = new Intl.PluralRules('ru-RU');
const reviewsPluralForm = new Map<Intl.LDMLPluralRule, string>([
  ['one', 'отзыв'],
  ['zero', 'отзывов'],
  ['two', 'отзыва'],
  ['few', 'отзыва'],
  ['many', 'отзывов'],
  ['other', 'отзывов'],
]);

const getReviewsText = (count: number, locale: string) => {
  const reviewsText = locale === 'kk' ? 'пікір' : reviewsPluralForm.get(reviewsPluralRule.select(count));
  const noReviewsText = locale === 'kk' ? 'Пікірлер жоқ' : 'Нет отзывов';

  return count ? `${count} ${reviewsText}` : noReviewsText;
};

const StarsLabel: React.FC<{ text: string }> = ({ text }) => {
  return (
    <span className={addReviewsLabel} data-qa="total-score-at-showcase-label_mf-pdp">
      {text}
    </span>
  );
};

export const RatingStars: React.FC<{
  totalScore: number;
  reviewsCount: number;
}> = ({ totalScore, reviewsCount }) => {
  const lang = getLang();
  return (
    <Rating
      value={roundRating(totalScore)}
      color={reviewsCount ? 'control-accent' : 'text-minor'}
      size="l"
      data-qa="total-score-at-showcase_mf-pdp"
    >
      <StarsLabel text={getReviewsText(reviewsCount, lang)} />
    </Rating>
  );
};

const Rating: React.FC<RatingProps> = ({ value, ...props }) => {
  const ref = useForceRenderEffect(value);

  return (
    <div ref={ref}>
      <FrontonRating value={value} {...props} />
    </div>
  );
};
